// Colour sets for the jk-21 Feature tonal illustration cards (see
// src/components/FeatureTonalSplash.vue). Unlike ILLUSTRATION_COLOURS
// (src/utils/illustrationColours.js), colour2 is always cream in every
// reference render — only the background/colour1 accent pair changes.
// Extracted from .illo-dump/4. Lines for splash cleaned/FeatureCard/
// Colour_Examples/*.png: 4 accent pairs, each shown with background/line
// swapped. Every value points at an existing --color-card-* custom
// property — no new tokens needed.
export const FEATURE_CARD_COLOURS = {
  yellow: {
    background: 'var(--color-card-yellow)',
    colour1: 'var(--color-card-orange)',
    colour2: 'var(--color-card-cream)',
  },
  orange: {
    background: 'var(--color-card-orange)',
    colour1: 'var(--color-card-yellow)',
    colour2: 'var(--color-card-cream)',
  },
  navy: {
    background: 'var(--color-card-navy)',
    colour1: 'var(--color-card-cyan)',
    colour2: 'var(--color-card-cream)',
  },
  cyan: {
    background: 'var(--color-card-cyan)',
    colour1: 'var(--color-card-navy)',
    colour2: 'var(--color-card-cream)',
  },
  purple: {
    background: 'var(--color-card-purple)',
    colour1: 'var(--color-card-pink)',
    colour2: 'var(--color-card-cream)',
  },
  pink: {
    background: 'var(--color-card-pink)',
    colour1: 'var(--color-card-purple)',
    colour2: 'var(--color-card-cream)',
  },
  green: {
    background: 'var(--color-card-green)',
    colour1: 'var(--color-card-lime)',
    colour2: 'var(--color-card-cream)',
  },
  lime: {
    background: 'var(--color-card-lime)',
    colour1: 'var(--color-card-green)',
    colour2: 'var(--color-card-cream)',
  },
  // Added 2026-08-07 for the Academy Programs cards (ContentCard.vue's
  // `program` variant) — `blue` is a real ContentCard colour (Governance
  // Program) with no match in the original 4-accent-pair set. cyan is the
  // closest tonal accent to this specific blue ($_blue, a vivid mid-blue).
  blue: {
    background: 'var(--color-card-blue)',
    colour1: 'var(--color-card-cyan)',
    colour2: 'var(--color-card-cream)',
  },
}

const FEATURE_CARD_COLOUR_KEYS = Object.keys(FEATURE_CARD_COLOURS)

// Simple deterministic string hash (djb2-ish) - turns a stable string (e.g.
// a route path) into an index. Used so a 'random' pick can agree between
// vite-ssg's server prerender and the client's post-hydration render: both
// compute the same hash from the same route, so they land on the same
// index. Without a seed there is no such agreement - see the note on
// resolveFeatureCardColour/resolveFeatureCardVariation below.
function hashSeed(seed) {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  }
  return hash
}

// Deterministically picks an item from any list using a seed — shared by
// resolveFeatureCardColour/resolveFeatureCardVariation below, and by any
// caller needing a stable pick from a smaller subset (e.g. ContentHero.vue's
// top-right splash tile, which only ever picks between variations 'C'/'E').
export function pickFromSeed(list, seed) {
  return list[hashSeed(seed) % list.length]
}

// Pass a `seed` (e.g. the current route's path) to get a value that agrees
// between SSR and hydration - required for any 'random' pick that ends up
// in real prerendered HTML (ContentHero.vue does this). Without a seed,
// falls back to plain Math.random() - fine for a caller that only ever
// renders client-side with no prerendered HTML to disagree with (e.g. the
// dev-only ComponentGallery demo).
export function resolveFeatureCardColour(colour, seed = '') {
  if (colour !== 'random') return colour
  return seed
    ? pickFromSeed(FEATURE_CARD_COLOUR_KEYS, seed)
    : FEATURE_CARD_COLOUR_KEYS[Math.floor(Math.random() * FEATURE_CARD_COLOUR_KEYS.length)]
}

// Which edge(s) of each Feature tonal variation's line art never touch the
// frame — same mechanism as SMALL_CARD_SAFE_EDGES (smallCardIllustrations.js),
// supplied directly by the user's own inspection of the artwork.
export const FEATURE_CARD_SAFE_EDGES = {
  A: ['right'],
  B: ['right'],
  C: ['top', 'right'],
  D: ['right'],
  E: ['top', 'right'],
}

export const FEATURE_CARD_VARIATIONS = Object.keys(FEATURE_CARD_SAFE_EDGES)

// Same seeding mechanism as resolveFeatureCardColour, for the variation
// axis - see that function's comment for why a seed matters.
export function resolveFeatureCardVariation(variation, seed = '') {
  if (variation !== 'random') return variation
  return seed
    ? pickFromSeed(FEATURE_CARD_VARIATIONS, seed)
    : FEATURE_CARD_VARIATIONS[Math.floor(Math.random() * FEATURE_CARD_VARIATIONS.length)]
}
